import {
  Icon,
  MouseOverContainer,
  prependZeroToSingleDigitNumber,
  shortenNumber,
  Text,
  TextFading,
  Tooltip
} from '@yes.technology/react-toolkit'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { HeightClass } from 'shared/types/toolkit'
import { ReactComponent as BrazilMiniMapSvg } from '../assets/mini-br.svg'
import {
  Container,
  EventsCountLabel,
  MapContainer,
  TitleContainer
} from './BrazilMiniMap.styles'
import { TooltipDictionary } from './hooks/useMapTooltipContent'

type BrazilMiniMapProps = {
  color?: string
  data: TooltipDictionary['nacional']
  tooltipHeightclass?: HeightClass
  expandedScale?: string
  isColumnLayout?: boolean
}

function getScaleValue(scaleString: string): number | null {
  const match = scaleString.match(/scale\(([\d.]+)\)/)
  if (match && match[1]) {
    return parseFloat(match[1])
  }
  return null
}

export type ConnectorPosition = React.ComponentProps<
  typeof MouseOverContainer
>['connectorPosition']

const BrazilMiniMap = ({
  color = '#096EB6',
  data,
  expandedScale,
  tooltipHeightclass,
  isColumnLayout = false
}: BrazilMiniMapProps) => {
  const { t } = useTranslation()

  const containerRef = useRef<HTMLDivElement>(null)

  const [coordinates, setCoordinates] = useState<
    | {
        top: number
        left: number
      }
    | undefined
  >(undefined)

  useEffect(() => {
    const numericScale = getScaleValue(expandedScale || '')
    const parentElement = containerRef?.current?.parentElement

    if (numericScale === null || !containerRef?.current || !parentElement) {
      setCoordinates(undefined)
      return
    }

    const parentRect = parentElement.getBoundingClientRect()

    const offsetYBase = numericScale > 1 ? -50 : 50
    const offsetY = offsetYBase / numericScale
    const offsetX = 30 / numericScale

    setCoordinates({
      top: (parentRect.top - data?.estimatedHeight) / numericScale + offsetY,
      left: -offsetX
    })

    return () => {
      setCoordinates(undefined)
    }
  }, [data?.estimatedHeight, expandedScale, containerRef])

  const {
    totalAmount: eventsCount = 0,
    title = '',
    html: description = ''
  } = data || {}

  const transformedCount =
    eventsCount < 10
      ? prependZeroToSingleDigitNumber(eventsCount)
      : shortenNumber(eventsCount)

  const hasTooltipContent = Boolean(title || description)

  return (
    <Container $isColumnLayout={isColumnLayout}>
      <TitleContainer>
        <Icon iconName='Pin' icSize='small' style={{ color }} />
        <TextFading>
          <Text variant='content-emphasis-03'>
            {t('map-container.national-event')}
          </Text>
        </TextFading>
      </TitleContainer>
      <div
        ref={containerRef}
        style={{ position: 'relative', textAlign: 'left' }}
      >
        {hasTooltipContent ? (
          <Tooltip
            title={title}
            description={description}
            connectorPosition='center'
            disablePointerEvents
            heightClass={tooltipHeightclass}
            customCoordinates={coordinates}
          >
            <MapContainer data-testid='map-container' $color={color}>
              <EventsCountLabel>{transformedCount}</EventsCountLabel>
              <BrazilMiniMapSvg />
            </MapContainer>
          </Tooltip>
        ) : (
          <MapContainer data-testid='map-container' $color={color}>
            <EventsCountLabel>{transformedCount}</EventsCountLabel>
            <BrazilMiniMapSvg />
          </MapContainer>
        )}
      </div>
    </Container>
  )
}

export default BrazilMiniMap
